import { useState, useEffect, useCallback, useRef } from "react";
import {
  ReactFlow,
  useNodesState,
  useEdgesState,
  Edge,
  Position,
  NodeTypes,
  OnEdgesChange,
  NodeMouseHandler,
  ReactFlowProvider,
  useReactFlow,
} from "@xyflow/react";
import dagre from "@dagrejs/dagre";

// Import the custom node component and its data type
import { MindMapNode } from "./MindMapNode";
import { CustomMindMapNode, MindMapNode as MindMapNodeType } from "./types";
import "@xyflow/react/dist/style.css";
import { NodeDescriptionModal } from "./NodeDescriptionModal";
import { BookOpen, GraduationCap } from "lucide-react";
import { CourseData } from "@packages/constant/CourseData";
import { useTranslation } from "react-i18next";
import { Text } from "@core/text";
import {
  getGetMindmapGeneratorQueryKey,
  useGenerateToolFromMindmapNode,
} from "@packages/api/madlenAPI";
import { useQueryClient } from "@tanstack/react-query";
import { GenerateToolFromNodeRequestDtoTargetToolType } from "@packages/api/madlenAPI.schemas";
// Helper function to build children map
function getChildrenMap(edges: Edge[]): Map<string, string[]> {
  const map = new Map<string, string[]>();
  edges.forEach((edge) => {
    const children = map.get(edge.source) || [];
    children.push(edge.target);
    map.set(edge.source, children);
  });
  return map;
}

// Helper function to convert hierarchical data to flat nodes array
function flattenNodes(
  nodes: MindMapNodeType[],
  parentId: string | null = null,
): MindMapNodeType[] {
  return nodes.reduce<MindMapNodeType[]>((acc, node) => {
    // Include current node
    acc.push(node);

    // Include children if they exist
    if (node.children && node.children.length > 0) {
      acc.push(...flattenNodes(node.children, node.id));
    }

    return acc;
  }, []);
}

// Helper function to generate edges from hierarchical data
function generateEdges(nodes: MindMapNodeType[]): Edge[] {
  const edges: Edge[] = [];

  // Process nodes and create edges based on parent-child relationships
  nodes.forEach((node) => {
    if (node.parent_id && node.parent_id !== "__ROOT__") {
      edges.push({
        id: `${node.parent_id}-${node.id}`,
        source: node.parent_id,
        target: node.id,
      });
    }
  });

  return edges;
}

const dagreGraph = new dagre.graphlib.Graph();
dagreGraph.setDefaultEdgeLabel(() => ({}));

// Adjust these values for desired spacing
const nodeWidth = 172;
const nodeHeight = 36;
function angleToDirection(
  angleRad: number,
):
  | "top"
  | "top-right"
  | "right"
  | "bottom-right"
  | "bottom"
  | "bottom-left"
  | "left"
  | "top-left" {
  const angleDeg = (angleRad * 180) / Math.PI;
  const normalized = (angleDeg + 360) % 360;

  if (normalized >= 337.5 || normalized < 22.5) return "right";
  if (normalized < 67.5) return "bottom-right";
  if (normalized < 112.5) return "bottom";
  if (normalized < 157.5) return "bottom-left";
  if (normalized < 202.5) return "left";
  if (normalized < 247.5) return "top-left";
  if (normalized < 292.5) return "top";
  return "top-right";
}

// Update function signature to use the specific CustomMindMapNode type
function getDiagonalLayoutedElements(
  nodes: CustomMindMapNode[],
  edges: Edge[],
): { nodes: CustomMindMapNode[]; edges: Edge[] } {
  const nodeMap = new Map(nodes.map((n) => [n.id, n]));
  const spacing = nodeWidth + 100;

  // Root node always goes at the center
  const root = nodes.find((n) => n.data.parent_id === "__ROOT__");
  if (!root) throw new Error("No root node found");

  root.position = { x: 0, y: 0 };
  root.sourcePosition = Position.Right;
  root.targetPosition = Position.Left;

  const positionedNodes = new Map<string, CustomMindMapNode>();
  positionedNodes.set(root.id, root);
  const octantOffsets: [number, number][] = [
    [0, -1], // ↑  up
    [1, -1], // ↗️  up-right
    [1, 0], // →  right
    [1, 1], // ↘️  down-right
    [0, 1], // ↓  down
    [-1, 1], // ↙️  down-left
    [-1, 0], // ←  left
    [-1, -1], // ↖️  up-left
  ];

  // Start with direct children of root
  const level1Children = nodes.filter((n) => n.data.parent_id === root.id);
  const radius = spacing * 1.5; // Circle radius
  level1Children.forEach((node, index) => {
    const angle = (2 * Math.PI * index) / level1Children.length;
    const posX = Math.cos(angle) * radius;
    const posY = Math.sin(angle) * radius;

    node.position = { x: posX, y: posY };

    const direction = angleToDirection(angle);
    const dx = Math.cos(angle) >= 0 ? 1 : -1;
    const dy = Math.sin(angle) >= 0 ? 1 : -1;

    positionedNodes.set(node.id, {
      ...node,
      data: {
        ...node.data,
        direction, // Store for later edge use
      },
    });

    layoutSubtree(node, posX, posY, dx, dy, 1);
  });
  function oppositeDirection(dir: string): string {
    const map: Record<string, string> = {
      top: "bottom",
      "top-right": "bottom-left",
      right: "left",
      "bottom-right": "top-left",
      bottom: "top",
      "bottom-left": "top-right",
      left: "right",
      "top-left": "bottom-right",
    };
    return map[dir] ?? "left";
  }

  function layoutSubtree(
    parent: CustomMindMapNode,
    parentX: number,
    parentY: number,
    dx: number,
    dy: number,
    depth: number,
  ) {
    const children = nodes.filter((n) => n.data.parent_id === parent.id);
    children.forEach((child, idx) => {
      const offsetX = parentX + dx * spacing * (depth + 1);
      const offsetY = parentY + dy * spacing * (idx + 1);

      child.position = { x: offsetX, y: offsetY };
      child.sourcePosition = Position.Right;
      child.targetPosition = Position.Left;

      positionedNodes.set(child.id, child);

      layoutSubtree(child, offsetX, offsetY, dx, dy, depth + 1);
    });
  }
  // Assign sourceHandle based on node position (left or right)
  const edgesWithHandles = edges.map((edge) => {
    const sourceNode = positionedNodes.get(edge.source);
    const targetNode = positionedNodes.get(edge.target);
    if (!sourceNode || !targetNode) return edge;

    const isRoot = sourceNode.data.parent_id === "__ROOT__";
    const targetDirection = targetNode.data.direction as string | undefined;

    let sourceHandle = "right-source";
    if (isRoot && targetDirection) {
      sourceHandle = `${targetDirection}-source`;
    } else {
      sourceHandle = sourceNode.position.x < 0 ? "left-source" : "right-source";
    }

    const targetHandle = targetDirection
      ? `${oppositeDirection(targetDirection)}-target`
      : targetNode.position.x < 0
        ? "right-target"
        : "left-target";

    return {
      ...edge,
      sourceHandle,
      targetHandle,
      style: {
        stroke: "#667eea", // A nice blue color
        strokeWidth: 2,
      },
      animated: true,
    };
  });

  return {
    nodes: Array.from(positionedNodes.values()),
    edges: edgesWithHandles,
  };
}

// Define the custom node types
const nodeTypes: NodeTypes = {
  mindmapNode: MindMapNode,
};

interface MindMapProps {
  data: MindMapNodeType[];
  imageUrl?: string;
  mindmapId: number;
  generatingToolId: { id: number; type: string } | null;
  setGeneratingToolId: (id: { id: number; type: string } | null) => void;
}

function MindMap({
  data,
  imageUrl,
  mindmapId,
  generatingToolId,
  setGeneratingToolId,
}: MindMapProps) {
  const { setCenter } = useReactFlow();
  const initialRenderRef = useRef(true);

  const { t } = useTranslation();
  const [nodes, setNodes, onNodesChange] = useNodesState<CustomMindMapNode>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  // State to hold the full data of the selected node for the modal
  const [selectedNodeData, setSelectedNodeData] =
    useState<CustomMindMapNode | null>(null);
  // State to track the ID of the tool currently being generated
  const findNode = (
    nodes: MindMapNodeType[],
    id: string,
  ): MindMapNodeType | null => {
    console.log("nodes", nodes);
    console.log("id", id);
    for (const node of nodes) {
      console.log("node SEARCHING FOR", node);
      if (node.id === id) {
        return node;
      }
      if (node.children) {
        const found = findNode(node.children, id);
        if (found) return found;
      }
    }
    return null;
  };
  const { mutate: generateToolFromMindmapNode } =
    useGenerateToolFromMindmapNode({
      mutation: {
        onSuccess: async (_d, variables) => {
          await queryClient.invalidateQueries({
            queryKey: getMindMapQueryKey,
          });
          //and now get the data from the query client
          const freshOutOfOvenData =
            queryClient.getQueryData(getMindMapQueryKey);
          console.log("freshOutOfOvenData", freshOutOfOvenData);

          const node = findNode(
            freshOutOfOvenData?.nodes,
            variables?.data?.nodeId,
          );
          console.log("node", node);
          if (node) {
            setGeneratingToolId({
              id: node.toolInfo?.id ?? 0,
              type: node.toolInfo?.toolType ?? "",
            });
          }
          console.log("Tool generated successfully");
        },
      },
    });

  const getMindMapQueryKey = getGetMindmapGeneratorQueryKey(mindmapId);
  const queryClient = useQueryClient();
  // --- Modal Handlers ---
  const showDescriptionModal = useCallback((node: CustomMindMapNode) => {
    setSelectedNodeData(node); // Store the whole node
    setIsModalOpen(true);
  }, []);
  const closeDescriptionModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedNodeData(null); // Clear selected node data
  }, []);
  // --- End Modal Handlers ---

  // --- Tool Generation Logic (Placeholder) ---
  const generateToolForNode = useCallback(
    (nodeId: string, toolId: string) => {
      // Prevent starting generation if another is already in progress
      if (generatingToolId) return;
      setGeneratingToolId({
        id: 0,
        type: toolId,
      });
      generateToolFromMindmapNode({
        mindmapId,
        data: {
          nodeId,
          targetToolType:
            toolId as GenerateToolFromNodeRequestDtoTargetToolType,
        },
      });
    },
    [setNodes, generatingToolId],
  );
  // --- End Tool Generation Logic ---

  // Update function to open modal on click
  const onNodeClickHandler: NodeMouseHandler = useCallback(
    (_event, node) => {
      // Cast node to CustomMindMapNode before passing
      showDescriptionModal(node as CustomMindMapNode);
    },
    [showDescriptionModal],
  );

  useEffect(() => {
    if (!data || data.length === 0) return;
    console.log("data", data);
    // Convert hierarchical data to flat structure
    const flatData = flattenNodes(data);

    // Generate ReactFlow nodes
    const initialNodesWithPosition = flatData.map((node) => ({
      id: node.id,
      type: "mindmapNode",
      data: {
        // Map from MindMapNode to data expected by MindMapNode component
        label: node.text,
        description: "", // No description in the new format
        id: node.id,
        parent_id: node.parent_id,
        text: node.text,
        children: node.children,
        toolInfo: node.toolInfo,
      },
      position: { x: 0, y: 0 }, // Default position, will be calculated by dagre
    })) as CustomMindMapNode[];

    // Add the description handler to nodes
    const nodesWithData = initialNodesWithPosition.map((node) => ({
      ...node,
      data: {
        ...node.data,
        onShowDescription: () => showDescriptionModal(node),
      },
    }));

    // Get edges from parent-child relationships
    const initialEdges = generateEdges(flatData);
    // Get layout for visible elements
    const layouted = getDiagonalLayoutedElements(nodesWithData, initialEdges);
    // if selectedNodeData is not null, set the node to the selected node
    if (selectedNodeData) {
      //find the node in the layouted nodes
      const selectedNode = layouted.nodes.find(
        (node) => node.id === selectedNodeData.id,
      );
      if (selectedNode) {
        setSelectedNodeData(selectedNode);
      }
    }
    // Update state
    setNodes(layouted.nodes);
    setEdges(layouted.edges);

    // Only center on root node during initial render
    if (initialRenderRef.current) {
      const rootNode = layouted.nodes.find(
        (n) => n.data.parent_id === "__ROOT__",
      );
      if (rootNode) {
        const { x, y } = rootNode.position;
        setCenter(x, y, {
          zoom: 0.5, // adjust this as needed
          duration: 800, // smooth animation
        });
      }
      initialRenderRef.current = false;
    }
  }, [data, setNodes, setEdges, showDescriptionModal, setCenter]);

  const handleEdgesChange: OnEdgesChange = onEdgesChange;

  return (
    <div className="relative w-full h-full">
      <img
        src={imageUrl}
        alt="mindmap"
        loading="eager"
        className="w-full h-full object-cover absolute top-0 left-0 opacity-30"
        style={{
          filter: "blur(4px)",
        }}
      />
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={handleEdgesChange}
        onNodeClick={onNodeClickHandler}
        nodeTypes={nodeTypes}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        zoomOnScroll={true}
        panOnScroll={false}
        panOnDrag={true}
        defaultEdgeOptions={{
          type: "smoothstep",
          style: {
            stroke: "#667eea",
            strokeWidth: 2,
          },
          animated: true,
        }}
        //fitView
        proOptions={{ hideAttribution: true }}
      />

      {isModalOpen && selectedNodeData && (
        <NodeDescriptionModal
          closeDescriptionModal={closeDescriptionModal}
          selectedNodeData={selectedNodeData}
          generatingToolId={generatingToolId}
          generateToolForNode={generateToolForNode}
        />
      )}
    </div>
  );
}

export default function MindMapWrapper({ props }: { props: MindMapProps }) {
  return (
    <ReactFlowProvider>
      <MindMap {...props} />
    </ReactFlowProvider>
  );
}
